import clsx from "clsx"
import { useMemo, type FC } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { BoxIcon } from "@/assets"
import { useGameLogic } from "@/hooks/useGameLogic"
import { useModal } from "@/modals"
import { useAppSelector } from "@/store"
import {
  CARD_SKIN_TYPES,
  useGetCardBackTemplateByTypeQuery
} from "@/store/apis/cardBacksAPI"
import { useGetGameplayStateQuery } from "@/store/apis/gameplayAPI"
import { useGetPersonalDeckInventoryCountsQuery } from "@/store/apis/personalDeckAPI"
import type { IStoreItem } from "@/store/apis/storeAPI"
import { STORE_ITEM_TYPES } from "@/store/apis/storeAPI"
import { GameplaySelectors } from "@/store/slices/gameplay"
import { Button } from "@/ui/Button"
import { CardView } from "@/ui/Card"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./MatchGoldCardItem.module.scss"

const modalType = "matchGoldCards"

interface IMatchGoldCardItemProps extends IStoreItem<STORE_ITEM_TYPES.CARD> {}

export const MatchGoldCardItem: FC<IMatchGoldCardItemProps> = ({
  data,
  dataId
}) => {
  const { modal } = useModal(modalType)

  const { t } = useTranslation()
  const { t: tCard } = useTranslation("cards")

  const { handlePlayCard, isMyTurn } = useGameLogic()
  const { data: cardsInventoryCounts } =
    useGetPersonalDeckInventoryCountsQuery()

  const sessionId = useAppSelector(GameplaySelectors.selectSessionId())
  const { data: gameState } = useGetGameplayStateQuery(
    { sessionId: sessionId ?? "" },
    { skip: !sessionId }
  )

  const { data: goldCardTemplate } = useGetCardBackTemplateByTypeQuery(
    CARD_SKIN_TYPES.GOLD
  )

  const cardCount = useMemo(() => {
    return cardsInventoryCounts?.[dataId] ?? 0
  }, [cardsInventoryCounts, dataId])

  const restriction = gameState?.cardRestrictions?.[data?.type ?? ""]
  const currentTurn = gameState?.turn ?? 0
  const isOnCooldown =
    !!restriction && restriction.cooldownEndsAtTurn > currentTurn
  const remainingTurns = isOnCooldown
    ? restriction.cooldownEndsAtTurn - currentTurn
    : 0

  const playCount = restriction?.playCount ?? 0
  const isExhausted = playCount >= (data?.maxUsesPerGame ?? Infinity)

  const description = useMemo(() => {
    if ((data?.maxUsesPerGame || Infinity) === 1) {
      return t(`cardCoolDown.oncePerMatch`, {
        maxUsesPerGame: data?.maxUsesPerGame
      })
    } else if ((data?.coolDownMovements || 0) > 1) {
      return t(
        `cardCoolDown.${(data?.maxUsesPerGame || 999) === 1 ? "oncePerMatch" : "coolDown"}`,
        {
          coolDownMovements: data?.coolDownMovements,
          turns: t("turn", {
            count: data?.maxUsesPerGame
          })
        }
      )
    }

    return
  }, [data, t])

  const handleUse = () => {
    if (isExhausted) return
    if (isOnCooldown) return
    if (!isMyTurn) {
      toast.warn("It's not your turn")
      return
    } else if (cardCount === 0) {
      toast.warn(
        "You don't have this card, buy it in the store after the match"
      )
      return
    }

    handlePlayCard(dataId)
    modal.close()
  }

  return (
    <div className={clsx(styles.MatchGoldCardItem)}>
      <div className={clsx(styles.MatchGoldCardItem_header)}>
        <div className={clsx(styles.MatchGoldCardItem_title)}>
          {tCard(`card.${data?.type}.title.nominative`)}
        </div>
        <div className={clsx(styles.MatchGoldCardItem_card)}>
          <CardView
            className={clsx(styles.MatchGoldCardItem_cardPreview)}
            data={{ imgSrc: data?.imgSrc, price: 0 }}
            title={tCard(`card.${data?.type}.title.nominative`)}
            description={tCard(`card.${data?.type}.description_short`)}
            skin={goldCardTemplate}
            isShowBackSide={false}
            isMatchActive={true}
          />
        </div>
        <div className={clsx(styles.MatchGoldCardItem_count)}>
          <BoxIcon />
          {cardCount}
        </div>
      </div>

      <div className={clsx(styles.MatchGoldCardItem_middle)}>
        <OutlinedText
          className={clsx(styles.MatchGoldCardItem_rule, {
            [styles[data?.type || ""]]: !!data
          })}
        >
          {tCard(`card.${data?.type}.description_short`, {
            defaultValue: data?.type
          })}
        </OutlinedText>

        <div className={clsx(styles.MatchGoldCardItem_text)}>{description}</div>
      </div>
      <Button
        className={clsx(styles.MatchGoldCardItem_button)}
        onClick={handleUse}
        disabled={isExhausted || isOnCooldown}
      >
        {isExhausted
          ? t("cardCoolDown.exhausted")
          : isOnCooldown
            ? t("cardCoolDown.active", {
                remainingTurns,
                turn: t("turn", { count: remainingTurns })
              })
            : t("button.apply")}
      </Button>
    </div>
  )
}
